import React, { useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Grid,
  Typography,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Drawer,
  LinearProgress,
  Badge,
  Button
} from '@mui/material';
import { FaSearch } from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';

import Actions from '../../constants/Actions';
import useDebounce from '../../utils/useDebounce';
import useResponsive from '../../utils/useResponsive';
import { countActiveFilters } from '../../utils/filterOptions';
import buscarProdutosComFiltros from '../../actions/v2/produtos/buscarProdutosComFiltros';
import listaCodigoFiliais from '../../actions/cadastro/lista-codigo-filiais';
import Products from '../../components/Products';
import ProductFilters from '../../components/ProductFilters';
import { initialFilters } from '../../reducers_dev';

function HomePage() {
  const dispatch = useDispatch();
  const search = useSelector(state => state.search);
  const filters = useSelector(state => state.filters);
  const filialOptions = useSelector(state => state.filialOptions);
  const productsPagination = useSelector(state => state.productsPagination);
  const loadingProducts = useSelector(state => state.loadingProducts);

  const isDesktop = useResponsive('up', 'md');
  const [searchInput, setSearchInput] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const debouncedFilters = useDebounce(filters, 400);
  const didMountRef = useRef(false);

  const searchText = Array.isArray(search) ? '' : (search || '');
  const hasSearched = searchText.length > 0;
  const activeFilters = countActiveFilters(filters);

  const hasMore = productsPagination?.hasMore || false;
  const currentPage = productsPagination?.page || 1;
  const pageSize = productsPagination?.pageSize || 60;
  const total = productsPagination?.total || 0;

  const handlePesquisar = () => {
    const trimmed = searchInput.trim();
    if (!trimmed) return;
    dispatch({ type: Actions.RESET_FILTERS });
    dispatch({ type: Actions.SEARCH, search: trimmed });
    buscarProdutosComFiltros(dispatch, {
      search: trimmed,
      filters: initialFilters,
      page: 1,
      silent: true
    });
  };

  const handleKeyUp = (e) => {
    if (e.key === 'Enter') handlePesquisar();
  };

  const handleLoadMore = () => {
    buscarProdutosComFiltros(dispatch, {
      search: searchText,
      filters: debouncedFilters,
      page: currentPage + 1,
      pageSize,
      silent: true
    });
  };

  useEffect(() => {
    if (!filialOptions || filialOptions.length === 0) {
      listaCodigoFiliais()
        .then(res => {
          dispatch({
            type: Actions.SET_FILIAL_OPTIONS,
            filialOptions: res.data || []
          });
        })
        .catch(err => console.error('Erro ao carregar filiais:', err));
    }
  }, []);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (!hasSearched) return;
    buscarProdutosComFiltros(dispatch, {
      search: searchText,
      filters: debouncedFilters,
      page: 1,
      silent: true
    });
  }, [debouncedFilters]);

  useEffect(() => {
    if (hasSearched && !searchInput) {
      setSearchInput(searchText);
    }
  }, []);

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {loadingProducts && <LinearProgress />}

      {!hasSearched && (
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: 22, md: 30 },
                color: '#1a1a2e',
                mb: 1
              }}
            >
              Buscar produtos
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 12, md: 14 },
                color: 'text.secondary',
                mb: 3
              }}
            >
              Pesquise por nome, marca, grupo ou código reduzido
            </Typography>
            <OutlinedInput
              fullWidth
              placeholder="Buscar por reduzido, nome ou marca"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyUp={handleKeyUp}
              sx={{
                bgcolor: '#fff',
                height: 52,
                borderRadius: 2,
                fontSize: 15,
                '& fieldset': { borderColor: '#1a1a2e' }
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={handlePesquisar}
                    sx={{
                      bgcolor: '#1a1a2e',
                      color: '#fff',
                      width: 40,
                      height: 40,
                      '&:hover': { bgcolor: '#2a2a3e' }
                    }}
                  >
                    <FaSearch size={16} />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>
        </Container>
      )}

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '85vw', sm: 320 } } }}
      >
        <ProductFilters onClose={() => setDrawerOpen(false)} />
      </Drawer>
    </Box>
  );
}

export default withRouter(HomePage);
